import './mobile-gallery-viewer.scss';

interface Options {
  selector?: string; // container that holds the grid (.gl-image__grid should be inside)
  debug?: boolean;
}

export default class MobileGalleryViewer {
  private selector: string;
  private container: HTMLElement | null = null;
  private grid: HTMLElement | null = null;
  private indicatorsWrap: HTMLElement | null = null;
  private indicators: HTMLElement[] = [];
  private rafId: number | null = null;
  private boundOnScroll: () => void;

  constructor(options: Options = {}) {
    // Prefer the lightbox images container by default; allow override.
    this.selector = options.selector || '.gallery-lightbox__images';
    this.boundOnScroll = this.onScroll.bind(this);

    // only run on small screens
    if (typeof window !== 'undefined' && window.matchMedia) {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
    }

    this.init();
  }

  private init(): void {
    this.container = document.querySelector(this.selector) as HTMLElement | null;
    // Try to find the container and the image grid. Be robust: the grid may
    // live elsewhere (lightbox) so fall back to global queries if needed.
    if (this.container) {
      this.grid = this.container.querySelector('.gl-image__grid') as HTMLElement | null;
    }

    // Fallbacks if initial selector didn't yield the grid
    if (!this.grid) {
      // Prefer explicit lightbox path, then any grid on the page
      this.grid = document.querySelector('.gallery-lightbox__images .gl-image__grid') as HTMLElement | null ||
                  document.querySelector('.gl-image__grid') as HTMLElement | null;

      // If we found the grid via fallback, ensure `container` references the parent
      if (this.grid && !this.container) {
        const parent = this.grid.closest('.gallery-lightbox__images') as HTMLElement | null;
        this.container = parent || (this.grid.parentElement as HTMLElement | null);
      }
    }

    if (!this.container || !this.grid) return;

    this.buildIndicators();
    this.bindEvents();
  }

  private buildIndicators(): void {
    if (!this.grid || !this.container) return;

    const count = this.grid.children.length || 0;
    if (count <= 1) return; // no need for indicators

    const wrap = document.createElement('div');
    wrap.className = 'mobile-gallery__pagination';

    for (let i = 0; i < count; i++) {
      const span = document.createElement('span');
      span.className = 'mobile-gallery__indicator';
      span.setAttribute('data-index', String(i));
      wrap.appendChild(span);
      this.indicators.push(span);
    }

    // Expose wrapper for styling; add a container class so scoping works
    this.container.classList.add('mobile-gallery-viewer');
    this.container.appendChild(wrap);
    this.indicatorsWrap = wrap;

    // Initialize active state
    this.updateIndicators(0);
  }

  private bindEvents(): void {
    if (!this.grid) return;

    this.grid.addEventListener('scroll', this.boundOnScroll, { passive: true });
    // initial tick to sync any non-scrolled state
    this.rafId = requestAnimationFrame(() => this.updateFromScroll());
  }

  private onScroll(): void {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.updateFromScroll());
  }

  private updateFromScroll(): void {
    if (!this.grid || this.indicators.length === 0) return;

    const scrollLeft = this.grid.scrollLeft;
    const width = this.grid.clientWidth || this.grid.getBoundingClientRect().width || 1;
    const index = Math.round(scrollLeft / width);
    this.updateIndicators(index);
  }

  private updateIndicators(activeIndex: number): void {
    this.indicators.forEach((el, i) => {
      el.classList.remove(
        'mobile-gallery__indicator--active',
        'mobile-gallery__indicator--near1',
        'mobile-gallery__indicator--near2',
        'mobile-gallery__indicator--far'
      );

      const distance = Math.abs(i - activeIndex);

      if (distance === 0) {
        el.classList.add('mobile-gallery__indicator--active');
      } else if (distance === 1) {
        el.classList.add('mobile-gallery__indicator--near1');
      } else if (distance === 2) {
        el.classList.add('mobile-gallery__indicator--near2');
      } else {
        el.classList.add('mobile-gallery__indicator--far');
      }
    });
  }

  public destroy(): void {
    if (this.grid) this.grid.removeEventListener('scroll', this.boundOnScroll);
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    if (this.indicatorsWrap && this.indicatorsWrap.parentNode) this.indicatorsWrap.parentNode.removeChild(this.indicatorsWrap);
    if (this.container) this.container.classList.remove('mobile-gallery-viewer');
    this.grid = null;
    this.container = null;
    this.indicators = [];
    this.indicatorsWrap = null;
  }
}
